import { useState, useEffect, useRef, useMemo } from "react";
import { useThemeContext } from "../../theme/ThemeContext";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { getMctRisk } from "../../utils/mctUtils";
import { toMins } from "../../utils/timeUtils";
import { overlayBackdrop } from "../../theme/styles";
import { TYPOGRAPHY, RADIUS } from "../../theme/tokens";
import { Button, AirlineLogo } from "../shared";
import saConnectionServiceHeader from "../../assets/sa-connection-service-header.png";
import saConnectionServiceHeaderLight from "../../assets/sa-connection-service-header-light.jpg";
import { isOpenStatus } from "../../utils/constants";
import { signNamesFromPassengers } from "../../utils/entryUtils";

const CITY_NAMES = {
  // Europe
  LHR: "LONDON",        LGW: "LONDON",        LCY: "LONDON",        MAN: "MANCHESTER",
  EDI: "EDINBURGH",     GLA: "GLASGOW",       DUB: "DUBLIN",        SNN: "SHANNON",
  FRA: "FRANKFURT",     MUC: "MUNICH",        BER: "BERLIN",        HAM: "HAMBURG",
  DUS: "DUSSELDORF",    ZRH: "ZURICH",        VIE: "VIENNA",        GVA: "GENEVA",
  CDG: "PARIS",         AMS: "AMSTERDAM",     BRU: "BRUSSELS",      LIS: "LISBON",
  MAD: "MADRID",        BCN: "BARCELONA",     AGP: "MALAGA",        ALC: "ALICANTE",
  SVQ: "SEVILLE",       BIO: "BILBAO",        PMI: "PALMA",         OPO: "PORTO",
  FCO: "ROME",          MXP: "MILAN",         VCE: "VENICE",        NAP: "NAPLES",
  BRI: "BARI",          CPH: "COPENHAGEN",    OSL: "OSLO",          ARN: "STOCKHOLM",
  HEL: "HELSINKI",      WAW: "WARSAW",        PRG: "PRAGUE",        BUD: "BUDAPEST",
  ATH: "ATHENS",        NCE: "NICE",          KEF: "REYKJAVIK",     DBV: "DUBROVNIK",
  SPU: "SPLIT",         LJU: "LJUBLJANA",
  // Middle East
  IST: "ISTANBUL",      SAW: "ISTANBUL",      DXB: "DUBAI",         DOH: "DOHA",
  AUH: "ABU DHABI",     RUH: "RIYADH",        TLV: "TEL AVIV",      AMM: "AMMAN",
  BEY: "BEIRUT",        KWI: "KUWAIT CITY",   BAH: "BAHRAIN",       CAI: "CAIRO",
  // Asia-Pacific
  NRT: "TOKYO - NARITA", HND: "TOKYO - HANEDA", KIX: "OSAKA",         NGO: "NAGOYA",
  ICN: "SEOUL",         GMP: "SEOUL",
  PEK: "BEIJING",       PKX: "BEIJING",       PVG: "SHANGHAI",      SHA: "SHANGHAI",
  CAN: "GUANGZHOU",     HKG: "HONG KONG",     TPE: "TAIPEI",
  SIN: "SINGAPORE",     KUL: "KUALA LUMPUR",  BKK: "BANGKOK",       DMK: "BANGKOK",
  MNL: "MANILA",        SGN: "HO CHI MINH",   HAN: "HANOI",         CGK: "JAKARTA",
  DEL: "NEW DELHI",     BOM: "MUMBAI",        MAA: "CHENNAI",       BLR: "BANGALORE",
  HYD: "HYDERABAD",     CCU: "KOLKATA",       CMB: "COLOMBO",       DAC: "DHAKA",
  RGN: "YANGON",        ULN: "ULAANBAATAR",
  // Oceania & Pacific Islands
  SYD: "SYDNEY",        MEL: "MELBOURNE",     BNE: "BRISBANE",      ADL: "ADELAIDE",
  PER: "PERTH",         AKL: "AUCKLAND",      CHC: "CHRISTCHURCH",
  HNL: "HONOLULU",      OGG: "MAUI",          KOA: "KONA",          LIH: "KAUAI",
  ITO: "HILO",          GUM: "GUAM",          SPN: "SAIPAN",        ROR: "PALAU",
  PPT: "PAPEETE",
  // Africa
  JNB: "JOHANNESBURG",  CPT: "CAPE TOWN",     NBO: "NAIROBI",       ADD: "ADDIS ABABA",
  LOS: "LAGOS",         ACC: "ACCRA",         CMN: "CASABLANCA",    TUN: "TUNIS",
  DSS: "DAKAR",         RAK: "MARRAKECH",
  // Canada
  YYZ: "TORONTO",       YVR: "VANCOUVER",     YUL: "MONTREAL",      YYC: "CALGARY",
  YEG: "EDMONTON",      YWG: "WINNIPEG",      YOW: "OTTAWA",        YQB: "QUEBEC CITY",
  YHZ: "HALIFAX",
  // US — Major hubs
  ORD: "CHICAGO",       ATL: "ATLANTA",       DFW: "DALLAS",        DEN: "DENVER",
  JFK: "NEW YORK",      LGA: "NEW YORK",      EWR: "NEWARK",        BOS: "BOSTON",
  IAD: "WASHINGTON",    DCA: "WASHINGTON",    BWI: "BALTIMORE",     PHL: "PHILADELPHIA",
  MIA: "MIAMI",         IAH: "HOUSTON",       HOU: "HOUSTON",       MSP: "MINNEAPOLIS",
  DTW: "DETROIT",       SFO: "SAN FRANCISCO", SEA: "SEATTLE",       PHX: "PHOENIX",
  // US — West
  LAS: "LAS VEGAS",     SAN: "SAN DIEGO",     SNA: "ORANGE COUNTY", BUR: "BURBANK",
  ONT: "ONTARIO",       PSP: "PALM SPRINGS",  SJC: "SAN JOSE",      OAK: "OAKLAND",
  SMF: "SACRAMENTO",    FAT: "FRESNO",        SBA: "SANTA BARBARA", SBP: "SAN LUIS OBISPO",
  BFL: "BAKERSFIELD",   MRY: "MONTEREY",      SLC: "SALT LAKE CITY",
  RNO: "RENO",          BOI: "BOISE",         PDX: "PORTLAND",      EUG: "EUGENE",
  MFR: "MEDFORD",       RDM: "BEND",          GEG: "SPOKANE",       ACV: "EUREKA",
  RDD: "REDDING",       OTH: "NORTH BEND",    ANC: "ANCHORAGE",
  ABQ: "ALBUQUERQUE",   ELP: "EL PASO",       TUS: "TUCSON",
  // US — Mountain / Ski
  ASE: "ASPEN",         EGE: "VAIL",          HDN: "STEAMBOAT SPRINGS", MTJ: "MONTROSE",
  DRO: "DURANGO",       GJT: "GRAND JUNCTION", SGU: "ST. GEORGE",   JAC: "JACKSON HOLE",
  IDA: "IDAHO FALLS",   TWF: "TWIN FALLS",    YKM: "YAKIMA",
  ALW: "WALLA WALLA",   PRC: "PRESCOTT",
  // US — Mountain States
  BIL: "BILLINGS",      BZN: "BOZEMAN",       MSO: "MISSOULA",      GTF: "GREAT FALLS",
  HLN: "HELENA",        LWS: "LEWISTON",      RAP: "RAPID CITY",    FSD: "SIOUX FALLS",
  FAR: "FARGO",         BIS: "BISMARCK",      GFK: "GRAND FORKS",   OMA: "OMAHA",
  DSM: "DES MOINES",
  // US — Midwest
  MCI: "KANSAS CITY",   STL: "ST. LOUIS",     MKE: "MILWAUKEE",     MSN: "MADISON",
  CMH: "COLUMBUS",      IND: "INDIANAPOLIS",  CVG: "CINCINNATI",    CLE: "CLEVELAND",
  PIT: "PITTSBURGH",    BUF: "BUFFALO",       ROC: "ROCHESTER",     SYR: "SYRACUSE",
  GRR: "GRAND RAPIDS",  FNT: "FLINT",         LAN: "LANSING",
  FWA: "FORT WAYNE",    SBN: "SOUTH BEND",    ICT: "WICHITA",       TVC: "TRAVERSE CITY",
  GRB: "GREEN BAY",     MLI: "MOLINE",        CID: "CEDAR RAPIDS",  DBQ: "DUBUQUE",
  LSE: "LA CROSSE",     BMI: "BLOOMINGTON",   SPI: "SPRINGFIELD",
  // US — Northeast
  ALB: "ALBANY",        BDL: "HARTFORD",      BTV: "BURLINGTON",    PVD: "PROVIDENCE",
  PWM: "PORTLAND",      BGR: "BANGOR",        MHT: "MANCHESTER",    SWF: "NEWBURGH",
  ABE: "ALLENTOWN",     AVP: "SCRANTON",      MDT: "HARRISBURG",    ERI: "ERIE",
  BGM: "BINGHAMTON",    ITH: "ITHACA",        ELM: "ELMIRA",        ORF: "NORFOLK",
  RIC: "RICHMOND",
  // US — Southeast
  MCO: "ORLANDO",       TPA: "TAMPA",         FLL: "FORT LAUDERDALE", RSW: "FORT MYERS",
  PBI: "WEST PALM BEACH", JAX: "JACKSONVILLE",  SRQ: "SARASOTA",
  GNV: "GAINESVILLE",   TLH: "TALLAHASSEE",   PNS: "PENSACOLA",     VPS: "FORT WALTON BEACH",
  MOB: "MOBILE",        GPT: "BILOXI",        MSY: "NEW ORLEANS",   BTR: "BATON ROUGE",
  JAN: "JACKSON",       BHM: "BIRMINGHAM",    HSV: "HUNTSVILLE",    CHS: "CHARLESTON",
  SAV: "SAVANNAH",      ILM: "WILMINGTON",    MYR: "MYRTLE BEACH",  GSP: "GREENVILLE",
  GSO: "GREENSBORO",    RDU: "RALEIGH",       CLT: "CHARLOTTE",     AVL: "ASHEVILLE",
  CAE: "COLUMBIA",      TYS: "KNOXVILLE",     LEX: "LEXINGTON",     SDF: "LOUISVILLE",
  EVV: "EVANSVILLE",    BNA: "NASHVILLE",     MEM: "MEMPHIS",
  // US — South / Central
  SAT: "SAN ANTONIO",
  AUS: "AUSTIN",        OKC: "OKLAHOMA CITY", TUL: "TULSA",         XNA: "FAYETTEVILLE",
  SGF: "SPRINGFIELD",   SHV: "SHREVEPORT",    CRP: "CORPUS CHRISTI", HRL: "HARLINGEN",
  LBB: "LUBBOCK",       MAF: "MIDLAND",       ABI: "ABILENE",
  // US — Territories
  SJU: "SAN JUAN",      BQN: "AGUADILLA",     STT: "ST. THOMAS",    STX: "ST. CROIX",
  // Mexico
  MEX: "MEXICO CITY",   GDL: "GUADALAJARA",   MTY: "MONTERREY",     CUN: "CANCUN",
  SJD: "LOS CABOS",     PVR: "PUERTO VALLARTA", MZT: "MAZATLAN",    ZIH: "IXTAPA",
  HMO: "HERMOSILLO",    BJX: "LEON",          CZM: "COZUMEL",       MID: "MERIDA",
  OAX: "OAXACA",        QRO: "QUERETARO",     ZLO: "MANZANILLO",    TQO: "TULUM",
  // Central America
  PTY: "PANAMA CITY",   GUA: "GUATEMALA CITY", SAL: "SAN SALVADOR", SJO: "SAN JOSE",
  LIR: "LIBERIA",       MGA: "MANAGUA",       SAP: "SAN PEDRO SULA", RTB: "ROATAN",
  // Caribbean
  NAS: "NASSAU",        MBJ: "MONTEGO BAY",   KIN: "KINGSTON",      PUJ: "PUNTA CANA",
  SXM: "ST. MAARTEN",   AUA: "ARUBA",         GCM: "GRAND CAYMAN",  BGI: "BRIDGETOWN",
  PLS: "PROVIDENCIALES", POS: "PORT OF SPAIN",
  // South America
  GRU: "SAO PAULO",     GIG: "RIO DE JANEIRO", EZE: "BUENOS AIRES", SCL: "SANTIAGO",
  LIM: "LIMA",          BOG: "BOGOTA",        MDE: "MEDELLIN",      GYE: "GUAYAQUIL",
  UIO: "QUITO",         GOH: "NUUK",
};

const AIRLINE_NAMES = {
  // Star Alliance
  UA: "United Airlines",      LH: "Lufthansa",            AC: "Air Canada",
  NH: "All Nippon Airways",   SQ: "Singapore Airlines",   TG: "Thai Airways",
  OZ: "Asiana Airlines",      CA: "Air China",            TK: "Turkish Airlines",
  AV: "Avianca",              CM: "Copa Airlines",        ET: "Ethiopian Airlines",
  LO: "LOT Polish Airlines",  SK: "SAS",                  MS: "EgyptAir",
  A3: "Aegean Airlines",      TP: "TAP Air Portugal",     OS: "Austrian Airlines",
  LX: "Swiss",                SN: "Brussels Airlines",    AI: "Air India",
  NZ: "Air New Zealand",      OU: "Croatia Airlines",
  // Common LAX carriers
  AA: "American Airlines",    DL: "Delta Air Lines",      AS: "Alaska Airlines",
  B6: "JetBlue",              WN: "Southwest Airlines",   HA: "Hawaiian Airlines",
  AM: "Aeroméxico",           BA: "British Airways",      VS: "Virgin Atlantic",
  AF: "Air France",           KL: "KLM",                  IB: "Iberia",
  KE: "Korean Air",           CX: "Cathay Pacific",       QF: "Qantas",
  EK: "Emirates",             EY: "Etihad Airways",       QR: "Qatar Airways",
  EN: "Air Dolomiti",
};

const SA_FONT = "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif";
const WINDOW_SECS = 60 * 60;

const isComplete = e => !isOpenStatus(e.status);

function curSecs(now) {
  return now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
}

function diffSecs(timeStr, now) {
  const m = toMins(timeStr);
  if (m === null) return null;
  let d = m * 60 - curSecs(now);
  if (d < -43200) d += 86400;
  return d;
}

function formatEta(secs) {
  if (secs === null) return "—";
  if (secs <= 0) {
    const ago = Math.abs(Math.floor(secs / 60));
    return ago === 0 ? "NOW" : `−${ago}m ago`;
  }
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

function etaColor(secs, SA) {
  if (secs === null) return SA.silver;
  if (secs <= 0) return SA.silver;
  if (secs <= 20 * 60) return "#D46060";
  if (secs <= 45 * 60) return "#E8A030";
  return SA.white;
}

function RiskBadge({ risk }) {
  if (!risk || risk === "SAFE") return null;
  const isRisk = risk === "AT_RISK";
  return (
    <span style={{
      display: "inline-block", padding: "1px 6px", borderRadius: 3,
      fontSize: TYPOGRAPHY.tiny, fontWeight: 700, letterSpacing: "0.05em",
      background: isRisk ? "rgba(212,96,96,0.18)" : "rgba(232,160,48,0.18)",
      color:      isRisk ? "#D46060"               : "#E8A030",
      border:     isRisk ? "1px solid rgba(212,96,96,0.35)" : "1px solid rgba(232,160,48,0.35)",
    }}>
      {risk.replace("_", " ")}
    </span>
  );
}

// 3 solid red arrows matching the official SA Connection Service branding
const CHEVRON_COUNT = 3;
const CHEVRON_RED   = "#C41230";

function Chevrons({ h }) {
  const chevW  = h * 1.05;
  const step   = h * 0.60;
  const totalW = (CHEVRON_COUNT - 1) * step + chevW;

  return (
    <svg width={totalW} height={h} viewBox={`0 0 ${totalW} ${h}`}
      style={{ display: "block", flexShrink: 0 }}>
      {Array.from({ length: CHEVRON_COUNT }).map((_, i) => {
        const x = i * step;
        const pts = [
          `${x},${h * 0.26}`,
          `${x + chevW * 0.62},${h * 0.04}`,
          `${x + chevW},${h * 0.50}`,
          `${x + chevW * 0.62},${h * 0.96}`,
          `${x},${h * 0.74}`,
          `${x + chevW * 0.20},${h * 0.50}`,
        ].join(" ");
        return <polygon key={i} points={pts} fill={CHEVRON_RED} />;
      })}
    </svg>
  );
}

// Scales its content down (never up) until it fits the box it's given, so the flight block
// never overlaps the names or gets clipped, whatever the screen (Duo unfolded, phone, iPad).
function FitBox({ children, align = "center", style }) {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const outer = outerRef.current, inner = innerRef.current;
    if (!outer || !inner) return;
    const fit = () => {
      const w = inner.scrollWidth, h = inner.scrollHeight;
      if (!w || !h) return;
      setScale(Math.min(1, outer.clientWidth / w, outer.clientHeight / h));
    };
    fit();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(fit);
    ro.observe(outer);
    ro.observe(inner);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={outerRef} style={{ position: "relative", overflow: "hidden", minWidth: 0, minHeight: 0,
      display: "flex", alignItems: align, ...style }}>
      <div ref={innerRef} style={{ transform: `scale(${scale})`, transformOrigin: `left ${align === "center" ? "center" : "top"}`,
        whiteSpace: "nowrap", flexShrink: 0 }}>
        {children}
      </div>
    </div>
  );
}

// Passenger names in one large column. If they don't all fit, the list crawls upward in a
// seamless loop (departure-board style) with soft fades at the edges; if they fit, it's still.
// Reduce Motion: no crawl, the list can be scrolled by hand instead.
function ScrollingNames({ names, baseSize, color, center }) {
  const boxRef  = useRef(null);
  const listRef = useRef(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const [listH, setListH] = useState(0);
  const reduceMotion = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  // Font: as large as the base size, but small enough that the longest name fits one line
  const longest = names.reduce((m, n) => Math.max(m, n.length), 1);
  const fontSize = Math.max(14, Math.round(box.w ? Math.min(baseSize, box.w / (longest * 0.63)) : baseSize));
  const lineH = Math.round(fontSize * 1.5);
  const gapH = lineH; // blank line between the end of the list and its repeat

  useEffect(() => {
    const el = boxRef.current, list = listRef.current;
    if (!el || !list) return;
    const measure = () => { setBox({ w: el.clientWidth, h: el.clientHeight }); setListH(list.offsetHeight); };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    ro.observe(list);
    return () => ro.disconnect();
  }, []);

  const overflowing = listH > 0 && box.h > 0 && listH > box.h;
  const crawl = overflowing && !reduceMotion;

  const trackRef = useRef(null);
  const namesKey = names.join("\n");
  useEffect(() => {
    const track = trackRef.current;
    if (!crawl || !track || !track.animate) return;
    const distance = listH + gapH;
    const pxPerSec = fontSize * 0.9; // about one name every 1.7s
    const anim = track.animate(
      [{ transform: "translateY(0)" }, { transform: `translateY(-${distance}px)` }],
      { duration: (distance / pxPerSec) * 1000, iterations: Infinity, easing: "linear", delay: 1500 }
    );
    return () => anim.cancel();
  // namesKey, not names: the parent re-renders every second (clock) with a new array, which
  // would restart the crawl (and its start delay) every second so it never moved.
  }, [crawl, listH, gapH, fontSize, namesKey]);

  const line = (n, i) => (
    <div key={i} style={{ fontSize, lineHeight: `${lineH}px`, fontWeight: 500, color,
      letterSpacing: "0.01em", whiteSpace: "nowrap" }}>{n}</div>
  );
  const fade = "linear-gradient(to bottom, transparent 0, #000 9%, #000 91%, transparent 100%)";

  return (
    <div ref={boxRef} style={{
      position: "relative", flex: 1, minHeight: 0, minWidth: 0, height: "100%",
      overflowY: overflowing && reduceMotion ? "auto" : "hidden",
      display: "flex", flexDirection: "column", justifyContent: crawl || !center ? "flex-start" : "center",
      ...(crawl ? { WebkitMaskImage: fade, maskImage: fade } : {}),
    }}>
      <div ref={trackRef} style={{ willChange: crawl ? "transform" : undefined }}>
        <div ref={listRef}>{names.map(line)}</div>
        {crawl && <><div style={{ height: gapH }} /><div aria-hidden="true">{names.map(line)}</div></>}
      </div>
    </div>
  );
}

function SignView({ entry, names, saLogoData, onBack }) {
  const [dims, setDims] = useState({ w: window.innerWidth, h: window.innerHeight });
  const [holdProgress, setHoldProgress] = useState(0);
  const [toggleHoldProgress, setToggleHoldProgress] = useState(0);
  const [signDark, setSignDark] = useState(true);
  const holdRef = useRef(null);
  const toggleHoldRef = useRef(null);

  useEffect(() => {
    const update = () => setDims({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  useEffect(() => () => {
    if (holdRef.current) clearInterval(holdRef.current);
    if (toggleHoldRef.current) clearInterval(toggleHoldRef.current);
  }, []);

  useEffect(() => {
    const el = document.documentElement;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen || el.msRequestFullscreen;
    if (req) req.call(el).catch(() => {});
    return () => {
      const exit = document.exitFullscreen || document.webkitExitFullscreen || document.mozCancelFullScreen || document.msExitFullscreen;
      if (exit && document.fullscreenElement) exit.call(document).catch(() => {});
    };
  }, []);

  const startHold = (e) => {
    e.preventDefault();
    if (holdRef.current) return;
    const start = Date.now();
    holdRef.current = setInterval(() => {
      const p = Math.min((Date.now() - start) / 3000, 1);
      setHoldProgress(p);
      if (p >= 1) {
        clearInterval(holdRef.current);
        holdRef.current = null;
        onBack();
      }
    }, 30);
  };

  const cancelHold = () => {
    if (holdRef.current) { clearInterval(holdRef.current); holdRef.current = null; }
    setHoldProgress(0);
  };

  const startToggleHold = (e) => {
    e.preventDefault();
    if (toggleHoldRef.current) return;
    const start = Date.now();
    toggleHoldRef.current = setInterval(() => {
      const p = Math.min((Date.now() - start) / 1000, 1);
      setToggleHoldProgress(p);
      if (p >= 1) {
        clearInterval(toggleHoldRef.current);
        toggleHoldRef.current = null;
        setToggleHoldProgress(0);
        setSignDark(d => !d);
      }
    }, 30);
  };

  const cancelToggleHold = () => {
    if (toggleHoldRef.current) { clearInterval(toggleHoldRef.current); toggleHoldRef.current = null; }
    setToggleHoldProgress(0);
  };

  const landscape = dims.w > dims.h;
  const M = Math.min(dims.w, dims.h);

  // All sign colors driven by signDark so toggling is one state flip
  const T = signDark ? {
    bg:            "#111",
    textPrimary:   "#FFFFFF",
    textAirline:   "rgba(255,255,255,0.75)",
    textLabel:     "rgba(255,255,255,0.45)",
    textGate:      "rgba(255,255,255,0.55)",
    textNames:     "rgba(255,255,255,0.90)",
    divider:       "rgba(255,255,255,0.15)",
    stripBorder:   "1px solid rgba(255,255,255,0.10)",
    dismissIdle:   "rgba(255,255,255,0.18)",
    dismissActive: "rgba(255,255,255,0.60)",
    progressBg:    "rgba(255,255,255,0.12)",
    progressFill:  "rgba(255,255,255,0.55)",
    toggleBg:      "rgba(255,255,255,0.08)",
    toggleBorder:  "rgba(255,255,255,0.20)",
    toggleColor:   "rgba(255,255,255,0.50)",
  } : {
    bg:            "#FFFFFF",
    textPrimary:   "#111111",
    textAirline:   "rgba(0,0,0,0.65)",
    textLabel:     "rgba(0,0,0,0.40)",
    textGate:      "rgba(0,0,0,0.45)",
    textNames:     "rgba(0,0,0,0.85)",
    divider:       "rgba(0,0,0,0.12)",
    stripBorder:   "1px solid rgba(0,0,0,0.08)",
    dismissIdle:   "rgba(0,0,0,0.22)",
    dismissActive: "rgba(0,0,0,0.55)",
    progressBg:    "rgba(0,0,0,0.10)",
    progressFill:  "rgba(0,0,0,0.35)",
    toggleBg:      "rgba(0,0,0,0.06)",
    toggleBorder:  "rgba(0,0,0,0.15)",
    toggleColor:   "rgba(0,0,0,0.40)",
  };

  const labelSz    = Math.round(M * 0.052);
  const logoSz     = Math.round(M * 0.095);
  const airNameSz  = Math.round(M * 0.058);
  const fltSz      = Math.round(M * 0.175);
  // In landscape the city line ("TO TOKYO - NARITA") must fit the wider viewport;
  // scale from dims.w so font shrinks proportionally instead of overflowing.
  const citySz     = landscape ? Math.round(dims.w * 0.065) : Math.round(M * 0.135);
  const gateSz     = Math.round(M * 0.052);
  const namesSz    = Math.round(M * 0.062);
  const pad        = Math.round(M * 0.045);
  const btnSz      = Math.max(28, Math.round(M * 0.038));
  const iconSz     = Math.round(btnSz * 0.42);

  const airlineCode = (entry.outAirline || (entry.outFlt || "").slice(0, 2)).toUpperCase();
  const airlineName = AIRLINE_NAMES[airlineCode] || airlineCode;
  const fltNum      = entry.outFlt ?? "—";
  const city        = CITY_NAMES[entry.dest] ?? entry.dest ?? "—";
  const gate        = entry.outGate;
  const hasNames = names.length > 0;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 9999,
      background: T.bg, display: "flex", flexDirection: "column",
      fontFamily: SA_FONT, userSelect: "none",
      transition: "background 0.25s",
      paddingTop: "env(safe-area-inset-top, 20px)",
    }}>

      {/* ── Header ── */}
      <img
        src={signDark ? saConnectionServiceHeader : saConnectionServiceHeaderLight}
        alt="Star Alliance Connection Service"
        style={{ width: "100%", display: "block", flexShrink: 0 }}
      />

      {/* ── Body ── */}
      {/* Flight block scales to fit its area; names get the rest and crawl if they overflow.
          Landscape: flight | names side by side (half each). Portrait: flight on top, names below. */}
      <div style={{
        flex: 1, minHeight: 0, display: "flex",
        flexDirection: landscape ? "row" : "column",
        padding: `${pad}px ${Math.round(pad * 1.1)}px`,
        // Side safe areas (iPhone Duo unfolded landscape: status bar takes ~84pt on the right)
        paddingLeft: `calc(${Math.round(pad * 1.1)}px + env(safe-area-inset-left, 0px))`,
        paddingRight: `calc(${Math.round(pad * 1.1)}px + env(safe-area-inset-right, 0px))`,
        gap: Math.round(M * 0.05),
        overflow: "hidden",
      }}>
        {/* Flight info */}
        <FitBox align={landscape || !hasNames ? "center" : "flex-start"}
          style={landscape
            ? { flex: hasNames ? "0 0 50%" : "1 1 auto", height: "100%" }
            : { flex: hasNames ? "0 0 52%" : "1 1 auto", width: "100%" }}>
          <div style={{
            fontSize: labelSz, fontWeight: 700, color: T.textLabel,
            letterSpacing: "0.01em", marginBottom: Math.round(labelSz * 0.5),
          }}>
            CONNECTING TO
          </div>
          {/* Airline logo + name */}
          <div style={{
            display: "flex", alignItems: "center",
            gap: Math.round(logoSz * 0.28),
            marginBottom: Math.round(airNameSz * 0.35),
          }}>
            <AirlineLogo iata={airlineCode} size={logoSz} />
            <span style={{
              fontSize: airNameSz, fontWeight: 700, color: T.textAirline,
              letterSpacing: "0.01em", fontFamily: SA_FONT,
            }}>
              {airlineName}
            </span>
          </div>
          <div style={{
            fontSize: fltSz, fontWeight: 900, color: T.textPrimary,
            letterSpacing: "-0.01em", lineHeight: 1,
            marginBottom: Math.round(fltSz * 0.06),
          }}>
            {fltNum}
          </div>
          <div style={{
            fontSize: citySz, fontWeight: 900, color: T.textPrimary,
            letterSpacing: "-0.01em", lineHeight: 1.05,
            marginBottom: gate ? Math.round(citySz * 0.28) : 0,
          }}>
            TO {city}
          </div>
          {gate && (
            <div style={{
              fontSize: gateSz, fontWeight: 400, color: T.textGate,
              letterSpacing: "0.04em",
            }}>
              GATE {gate}
            </div>
          )}
        </FitBox>

        {/* Passenger names */}
        {hasNames && (
          <div style={{
            flex: "1 1 0", minWidth: 0, minHeight: 0, display: "flex",
            ...(landscape
              ? { borderLeft: `2px solid ${T.divider}`, paddingLeft: Math.round(M * 0.04) }
              : { borderTop: `2px solid ${T.divider}`, paddingTop: Math.round(pad * 0.6) }),
          }}>
            <ScrollingNames names={names} baseSize={Math.round(namesSz * 1.15)} color={T.textNames} center={landscape} />
          </div>
        )}
      </div>

      {/* ── Theme toggle — lower left ── */}
      <button
        onPointerDown={startToggleHold}
        onPointerUp={cancelToggleHold}
        onPointerLeave={cancelToggleHold}
        onPointerCancel={cancelToggleHold}
        style={{
          position: "absolute",
          bottom: Math.round(M * 0.01),
          left: Math.round(M * 0.022),
          width: btnSz, height: btnSz,
          borderRadius: "50%",
          background: toggleHoldProgress > 0
            ? (signDark ? `rgba(255,255,255,${0.08 + toggleHoldProgress * 0.28})` : `rgba(0,0,0,${0.06 + toggleHoldProgress * 0.22})`)
            : T.toggleBg,
          border: `1px solid ${T.toggleBorder}`,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: toggleHoldProgress > 0
            ? (signDark ? `rgba(255,255,255,${0.5 + toggleHoldProgress * 0.5})` : `rgba(0,0,0,${0.4 + toggleHoldProgress * 0.5})`)
            : T.toggleColor,
          padding: 0, zIndex: 10,
          touchAction: "none",
          transition: toggleHoldProgress > 0 ? "none" : "background 0.25s, border-color 0.25s, color 0.25s",
        }}
        title={signDark ? "Hold to switch to light mode" : "Hold to switch to dark mode"}
      >
        {signDark ? (
          <svg width={iconSz} height={iconSz} viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="2.8" stroke="currentColor" strokeWidth="1.4"/>
            <path d="M8 1.5V3M8 13V14.5M14.5 8H13M3 8H1.5M12.5 3.5L11.4 4.6M4.6 11.4L3.5 12.5M12.5 12.5L11.4 11.4M4.6 4.6L3.5 3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
          </svg>
        ) : (
          <svg width={iconSz} height={iconSz} viewBox="0 0 16 16" fill="none">
            <path d="M13.5 9.5A5.5 5.5 0 016.5 2.5a5.5 5.5 0 000 11 5.5 5.5 0 007-4z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round"/>
          </svg>
        )}
      </button>

      {/* ── Hold-to-dismiss strip ── */}
      <div
        onPointerDown={startHold}
        onPointerUp={cancelHold}
        onPointerLeave={cancelHold}
        onPointerCancel={cancelHold}
        style={{
          flexShrink: 0, touchAction: "none", userSelect: "none",
          display: "flex", flexDirection: "column", alignItems: "center",
          padding: `${Math.round(M * 0.014)}px ${Math.round(M * 0.02)}px ${Math.round(M * 0.022)}px`,
          borderTop: T.stripBorder, cursor: "pointer", gap: Math.round(M * 0.008),
        }}
      >
        <div style={{
          fontSize: Math.max(10, Math.round(M * 0.013)),
          color: holdProgress > 0 ? T.dismissActive : T.dismissIdle,
          letterSpacing: "0.12em",
          transition: "color 0.15s",
        }}>
          TAP AND HOLD TO DISMISS
        </div>
        <div style={{
          width: "38%", height: 2, borderRadius: 1,
          background: T.progressBg, overflow: "hidden",
          opacity: holdProgress > 0 ? 1 : 0,
          transition: holdProgress > 0 ? "none" : "opacity 0.3s",
        }}>
          <div style={{
            height: "100%", borderRadius: 1,
            width: `${holdProgress * 100}%`,
            background: T.progressFill,
          }} />
        </div>
      </div>
    </div>
  );
}

export function ExpeditorModal({ entries, onClose, saLogoData }) {
  const { SA, GLASS } = useThemeContext();
  const [signEntry, setSignEntry] = useState(null);
  const [now, setNow]             = useState(() => new Date());
  // { entryKey: "Name1\nName2" } — only connections whose names were edited here. Untouched
  // ones use their TDT passengers ("J. Monas", "Johnson Party of 4"), so names synced later show up.
  const [namesMap, setNamesMap]   = useState({});
  const tdtNames = (e) => signNamesFromPassengers(e.passengers).join("\n");
  const namesFor = (e, key) => namesMap[key] ?? tdtNames(e);
  const [expandedId, setExpandedId] = useState(null);

  useLockBodyScroll();
  useEscapeKey(() => { if (signEntry) setSignEntry(null); else onClose(); });

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Wake Lock while sign is active
  useEffect(() => {
    if (!signEntry) return;
    let lock;
    navigator.wakeLock?.request("screen").then(l => { lock = l; }).catch(() => {});
    return () => { lock?.release(); };
  }, [signEntry]);

  // All active entries with ETA within ±45 min, sorted soonest first
  const inWindow = useMemo(() => {
    const cs = curSecs(now);
    return entries
      .filter(e => {
        if (isComplete(e)) return false;
        const m = toMins(e.eta || e.sta);
        if (m === null) return false;
        let d = m * 60 - cs;
        if (d < -43200) d += 86400;
        return d >= -WINDOW_SECS && d <= WINDOW_SECS;
      })
      .sort((a, b) => {
        const ds = e => {
          const m = toMins(e.eta || e.sta);
          if (m === null) return 9999 * 60;
          let d = m * 60 - cs;
          if (d < -43200) d += 86400;
          return d;
        };
        return ds(a) - ds(b);
      });
  }, [entries, now]);

  if (signEntry) {
    const entryKey = signEntry.id ?? `${signEntry.inFlt}_${signEntry.outFlt}`;
    const rawNames = namesFor(signEntry, entryKey);
    const nameList = rawNames.split("\n").map(n => n.trim()).filter(Boolean);
    return <SignView entry={signEntry} names={nameList} saLogoData={saLogoData} onBack={() => setSignEntry(null)} />;
  }

  return (
    <div style={{ ...overlayBackdrop, zIndex: 3000, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={onClose}>
      <div
        style={{ ...GLASS.modal, width: "min(520px, 96vw)", maxHeight: "88vh",
          display: "flex", flexDirection: "column", overflow: "hidden" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: "16px 20px 12px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <span style={{ fontSize: TYPOGRAPHY.sub, fontWeight: 700, color: SA.white,
              letterSpacing: "0.08em", fontFamily: SA_FONT }}>EXPEDITOR MODE</span>
            {inWindow.length > 0 && (
              <span style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver }}>
                {inWindow.length} flight{inWindow.length !== 1 ? "s" : ""} · ETA ±1 hr
              </span>
            )}
          </div>
          <Button variant="icon" onClick={onClose}>×</Button>
        </div>

        {/* List */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          {inWindow.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 24px", color: SA.silver,
              fontSize: TYPOGRAPHY.body, lineHeight: 1.6 }}>
              No connections arriving within ±45 minutes.
            </div>
          ) : (
            inWindow.map((e, i) => {
              const d       = diffSecs(e.eta || e.sta, now);
              const risk    = getMctRisk(e);
              const arrived = d !== null && d <= 0;
              const key     = e.id ?? `${e.inFlt}_${e.outFlt}`;
              const isOpen  = expandedId === key;
              const rawNames = namesFor(e, key);
              const tdtDefault = tdtNames(e);
              const edited = namesMap[key] !== undefined && namesMap[key] !== tdtDefault;
              const nameCount = rawNames.split("\n").map(n => n.trim()).filter(Boolean).length;

              return (
                <div key={key} style={{
                  borderBottom: i < inWindow.length - 1 ? `1px solid ${SA.borderGray}` : "none",
                }}>
                  {/* Main row */}
                  <div style={{ padding: "14px 20px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                    {/* Left: inbound info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {/* Inbound flight + origin */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3 }}>
                        <span style={{ fontSize: TYPOGRAPHY.sub, fontWeight: 700, color: SA.white, fontFamily: SA_FONT }}>
                          {e.inAirline} {e.inFlt}
                        </span>
                        <span style={{ fontSize: TYPOGRAPHY.body, color: SA.silver }}>
                          from {e.origin || "—"}
                        </span>
                      </div>

                      {/* ETA countdown + gate + risk */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 5, flexWrap: "wrap" }}>
                        <span style={{ fontSize: TYPOGRAPHY.note, fontWeight: 700, fontFamily: SA_FONT,
                          color: arrived ? SA.silver : etaColor(d, SA) }}>
                          {arrived ? formatEta(d) : `ETA ${formatEta(d)}`}
                        </span>
                        {e.inGate && (
                          <span style={{ fontSize: TYPOGRAPHY.body, color: SA.silver }}>· Gate {e.inGate}</span>
                        )}
                        {risk && <RiskBadge risk={risk} />}
                      </div>

                      {/* Outbound */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontSize: TYPOGRAPHY.body, color: SA.borderGray }}>→</span>
                        <span style={{ fontSize: TYPOGRAPHY.body, fontWeight: 600, color: SA.lightSilver, fontFamily: SA_FONT }}>
                          {e.outAirline} {e.outFlt}
                        </span>
                        <span style={{ fontSize: TYPOGRAPHY.body, color: SA.silver }}>
                          to {CITY_NAMES[e.dest] ?? e.dest ?? "—"}
                        </span>
                        {e.outGate && (
                          <span style={{ fontSize: TYPOGRAPHY.body, color: SA.silver }}>· Gate {e.outGate}</span>
                        )}
                        {e.ttlPax > 0 && (
                          <span style={{ fontSize: TYPOGRAPHY.body, color: SA.borderGray }}>· {e.ttlPax} PAX</span>
                        )}
                      </div>
                    </div>

                    {/* Right: names toggle + sign button */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
                      <Button variant="secondary" size="sm" onClick={() => setSignEntry(e)}
                        style={{ letterSpacing: "0.04em", fontFamily: SA_FONT }}>
                        Sign →
                      </Button>
                      <button
                        onClick={() => setExpandedId(isOpen ? null : key)}
                        style={{
                          background: "none", border: "none", cursor: "pointer", padding: "2px 4px",
                          fontSize: TYPOGRAPHY.caption, color: isOpen ? SA.white : SA.borderGray,
                          fontFamily: SA_FONT, letterSpacing: "0.04em",
                          display: "flex", alignItems: "center", gap: 4,
                        }}
                        title="Passenger names on the sign"
                      >
                        <svg width="11" height="11" viewBox="0 0 16 16" fill="none">
                          <circle cx="8" cy="5.5" r="2.5" stroke="currentColor" strokeWidth="1.4"/>
                          <path d="M2.5 13.5C2.5 11.015 5.015 9 8 9C10.985 9 13.5 11.015 13.5 13.5"
                            stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                          <path d="M12 6.5V10.5M10 8.5H14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                        </svg>
                        {nameCount > 0 ? `${nameCount}` : "Names"}
                      </button>
                    </div>
                  </div>

                  {/* Names input (expandable) */}
                  {isOpen && (
                    <div style={{ padding: "0 20px 14px" }}>
                      <textarea
                        autoFocus
                        value={rawNames}
                        onChange={ev => setNamesMap(m => ({ ...m, [key]: ev.target.value }))}
                        placeholder={"Enter passenger names, one per line\ne.g. M. Santos\nJohnson Party of 3"}
                        rows={Math.min(8, Math.max(3, nameCount + 1))}
                        style={{
                          width: "100%", boxSizing: "border-box",
                          background: "rgba(255,255,255,0.07)",
                          border: `1px solid ${SA.borderGray}`,
                          borderRadius: RADIUS.sm,
                          color: SA.white, padding: "10px 12px",
                          fontSize: TYPOGRAPHY.body, fontFamily: SA_FONT,
                          lineHeight: 1.6, resize: "vertical", outline: "none",
                        }}
                      />
                      {tdtDefault && (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                          marginTop: 6, fontSize: TYPOGRAPHY.caption, color: SA.borderGray, fontFamily: SA_FONT }}>
                          <span>{edited ? "Edited — not the TDT list" : `From TDT · ${e.passengers.length} passenger${e.passengers.length !== 1 ? "s" : ""}`}</span>
                          {edited && (
                            <button onClick={() => setNamesMap(m => { const n = { ...m }; delete n[key]; return n; })}
                              style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
                                fontSize: TYPOGRAPHY.caption, color: "#5b8eff", fontFamily: SA_FONT }}>
                              Use TDT names
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
